import { Carousel } from "primereact/carousel";
import { Paginator } from "primereact/paginator";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  COURSE_CATEGORIES,
  COURSE_TYPE,
} from "../../constants/course";
import { translate } from "../../localization/translateService";
import { getSeriesList } from "../../services/vodSeriesService";
import { getCourseCategories } from "../../services/lookupService";
import VodSeriesItem from "./VodSeriesItem";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { initAndSubscribeShoppingCart, markCoursesInCart } from "../../helpers/shoppingCartHelper";
import { Toast } from "primereact/toast";
import KeduLoading from "../loading/KeduLoading";
import NoData from "../nodata/NoData";
import { useUrlParam } from "../../hook/useUrlParam";
import { useScrollRestore } from "../../hook/useScrollRestore";
import { VOD_SERIES_PAGE } from "../../constants/app-constants";

const ROWS_PER_PAGE = 12;

const VodSeriesList = ({ props }) => {
  const [suggestedCourses, setSuggestedCourses] = useState(null);
  const toast = useRef(null);
  const [courses, setCourses] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState();
  const [first, setFirst] = useState(0);
  const [courseCategories, setCourseCategories] = useState([]);
  const [translationLoaded, setTranslationLoaded] = useState(false);
  const [translations, setTranslations] = useState({   
    ADD_SUCCESS: "",
    ADD_FAIL: "",
    PDF_AVAILABLE: "",
    freeLabel: "",
    boughtLabel: "",
    removeItemSuccessLabel: "",
    removeItemLabel: "",
    removeCartItemHeader: "",
    cancelButtonLabel: "",
    confirmButtonLabel: ""
  });
  const [shoppingCart, setShoppingCart] = useState();

  const { getParam: getCategory, setParam: setCategory } = useUrlParam(VOD_SERIES_PAGE.CATEGORY);
  const { getParam: getPage, setParam: setPage } = useUrlParam(VOD_SERIES_PAGE.FIRST);

  const { saveScrollPosition } = useScrollRestore(VOD_SERIES_PAGE.VOD_SCROLL_POSITION);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const addSuccess = await translate("UMBRACO_LANDING.VOD.ADD_TO_CART_SUCCESS");
        const addFail = await translate("UMBRACO_LANDING.VOD.ADD_TO_CART_FAIL");
        const pdfAvailable = await translate("UMBRACO_LANDING.VOD.PDF_AVAILABLE");
        const freeLabel = await translate("COURSES.LABELS.FREE");
        const boughtLabel = await translate("COURSES.LABELS.PURCHASED");
        const removeItemSuccessLabel = await translate("SHOPPING_CARTS.LABELS.REMOVE_ITEM_SUCCESS");
        const removeItemLabel = await translate("SHOPPING_CARTS.LABELS.REMOVE_ITEM");
        const removeCartItemHeader = await translate("SHOPPING_CARTS.LABELS.REMOVE_ITEM_HEADER");
        const cancelButtonLabel = await translate("BUTTON.CANCEL");
        const confirmButtonLabel = await translate("BUTTON.CONFIRM");
        setTranslations({
          ADD_SUCCESS: addSuccess,
          ADD_FAIL: addFail,
          PDF_AVAILABLE: pdfAvailable,
          freeLabel,
          boughtLabel,
          removeItemSuccessLabel,
          removeItemLabel,
          removeCartItemHeader,
          cancelButtonLabel,
          confirmButtonLabel
        });
      } catch (error) {
      } finally {
        setTranslationLoaded(true);
      }
    };
    loadTranslations();
  }, []);

  const fetchCourseCategories = useCallback(async () => {
    const categories = await getCourseCategories();
    const translatedCategories = await Promise.all(
      categories.map(async (category) => {
        return {
          label: await translate(category.description),
          value: category.description,
        };
      })
    );
    const allCategory = COURSE_CATEGORIES.ALL;
    allCategory.label = await translate(allCategory.label);
    setCourseCategories([allCategory, ...translatedCategories]);
  }, []);

  useEffect(() => {
    fetchCourseCategories();
  }, [fetchCourseCategories]);

  useEffect(() => {
    const loadSuggested = async () => {
      try {
        const MAX_SUGGESTED_COURSES = 12;
        const params = {
          first: 0,
          rows: MAX_SUGGESTED_COURSES,
          filters: {
            isSuggested: { value: true },
            type: { value: COURSE_TYPE.VIDEO_SERIES },
          },
        };
        const data = await getSeriesList(params);
        setSuggestedCourses(markCoursesInCart(data.data, shoppingCart));
      } catch (error) { }
    };
    loadSuggested();
  }, []);

  const loadCourses = useCallback(
    async (offset) => {
      const filters = { type: { value: COURSE_TYPE.VIDEO_SERIES } };
      if (selectedCategory !== COURSE_CATEGORIES.ALL.code) {
        filters.category = { value: selectedCategory };
      }
      try {
        const params = { first: offset, rows: ROWS_PER_PAGE, filters };
        const data = await getSeriesList(params);
        setCourses(markCoursesInCart(data.data, shoppingCart));
        setTotalRecords(data.pagingInfo.totalRecordCount);
      } catch { }
    },
    [selectedCategory]
  );

  useEffect(() => {
    setFirst(0);
    setPage(0);
    loadCourses(0);
  }, [selectedCategory, loadCourses]);

  // Initialize and subscribe to shopping cart updates (only if logged in inside helper)
  useEffect(() => {
    let unsubscribe = null;
    initAndSubscribeShoppingCart(setShoppingCart).then((unsub) => {
      unsubscribe = unsub;
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Recompute InCart on cart updates
  useEffect(() => {
    if (courses) {
      setCourses(markCoursesInCart(courses, shoppingCart));
    }
    if (suggestedCourses) {
      setSuggestedCourses(markCoursesInCart(suggestedCourses, shoppingCart));
    }
  }, [shoppingCart]);

  const onPageChange = (e) => {
    setFirst(e.first);
    setPage(e.first);
    loadCourses(e.first);
    const element = document.getElementById("vod-page-divider");
    if (element) {
      const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const y = element.getBoundingClientRect().top + window.pageYOffset - 8.6 * remInPx;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const savedCategory = getCategory();
    if (savedCategory) {
      setSelectedCategory(savedCategory);
    }

    const savedPage = getPage();
    if (savedPage) {
      setFirst(savedPage);
    }
  }, []);

  const carouselItemTemplate = (course) => {
    return (
      <div className="p-3 h-full">
        <VodSeriesItem
          course={course}
          toast={toast}
          translations={translations}
          onClick={() => {
            saveScrollPosition();
            const language = getI18nLanguageFromLocalStorage();
            const url = new URL(`/${language}/vod-series/${course.publicId}`, window.location.origin);
            url.searchParams.set("watch", course.publicId);
            window.open(url.toString(), "_self");
          }}
        />
      </div>
    );
  };

  const paginatorTemplate = {
    layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
    FirstPageLink: (options) => (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <i className="pi pi-angle-double-left"></i>
      </button>
    ),
    PrevPageLink: (options) => (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <i className="pi pi-angle-left"></i>
      </button>
    ),
    PageLinks: (options) => (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
      >
        {options.page + 1}
      </button>
    ),
    NextPageLink: (options) => (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <i className="pi pi-angle-right"></i>
      </button>
    ),
    LastPageLink: (options) => (
      <button
        type="button"
        className={options.className}
        onClick={options.onClick}
        disabled={options.disabled}
      >
        <i className="pi pi-angle-double-right"></i>
      </button>
    ),
  };

  if (!translationLoaded) {
    return null;
  }

  const renderSuggestedCourses = () => {
    return (
      <div className="keduall-carousel-container">
        {suggestedCourses.length === 0 ? (
          <NoData message={props.noRecordText} />
        ) : (
          <Carousel
            value={suggestedCourses}
            itemTemplate={carouselItemTemplate}
            numVisible={3}
            numScroll={3}
            className="keduall-carousel"
            responsiveOptions={[
              { breakpoint: "1024px", numVisible: 3, numScroll: 3 },
              { breakpoint: "768px", numVisible: 2, numScroll: 2 },
              { breakpoint: "560px", numVisible: 1, numScroll: 1 },
            ]}
            showIndicators={suggestedCourses.length > 3}
            showNavigators={suggestedCourses.length > 3}
          />
        )}
      </div>
    )
  }

  const renderCourses = () => {
    return (
      <>
        {courses.length === 0 ? (
          <NoData message={props?.noRecordText} />
        ) : (
          <div className="grid w-full" style={{ margin: '0 auto' }}>
            {courses.map((course) => (
              <div key={course.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-3">
                <VodSeriesItem
                  course={course}
                  onClick={() => {
                    saveScrollPosition();
                    const language = getI18nLanguageFromLocalStorage();
                    const url = new URL(`/${language}/vod-series/${course.publicId}`, window.location.origin);
                    url.searchParams.set("watch", course.publicId);
                    window.open(url.toString(), "_self");
                  }}
                  toast={toast}
                  translations={translations}
                />
              </div>
            ))}
          </div>
        )}
        {(totalRecords > ROWS_PER_PAGE  || first > 0) &&
          <Paginator
            first={first}
            rows={ROWS_PER_PAGE}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            template={paginatorTemplate}
            className="keduall-paginator"
          />
        }</>
    )
  }

  return (
    <div className="flex flex-column align-content-center justify-content-center main-content-wrapper">
      <div className="page-title-container">
        <h1 className="keduall-typography heading-h1-semibold-600 text-primary-txt-color mb-075 animate__animated animate__fadeInDown">{props?.pageTitle}</h1>
        <h4 className="keduall-typography heading-h4-normal-400 text-default-txt-color animate__animated animate__fadeInDown">{props?.pageSubTitle}</h4>
      </div>
      {suggestedCourses ? renderSuggestedCourses() : <KeduLoading />}
      <hr id="vod-page-divider" className="vod-page-divider" />

      <div className="level-tabs-container keduall-carousel-container">
        {courseCategories.map((cat) => {
          const isSelected = cat.value === selectedCategory;
          return (
            <button
              key={cat.value}
              className={`level-tab ${isSelected ? "selected" : ""}`}
              onClick={() => {
                setSelectedCategory(cat.value);
                setCategory(cat.value);
                setFirst(0);
                setPage(0);
              }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
      {courses ? renderCourses() : <KeduLoading />}
      <Toast ref={toast} position="bottom-right" className="keduall-toast" />
      <ConfirmDialog className="keduall-confirm-dialog" draggable={false} />
    </div>
  );
};

export default VodSeriesList; 